import moment from 'moment'
import { call, put, select } from 'redux-saga/effects'

import { generatePassword } from 'core/service/pdf-generation/utils/digital-signature'
import { partnerApiProposal, quickQuotePolicy } from 'core/service-wrapper/partner-api/handler-request/service-proposal'
import { partnerApiRequest } from 'core/service-wrapper/partner-api/handler-request/service-request'
import { getIdentityUser, isLoggedIn } from 'identity/selectors'
import { loginRequest } from 'quick-quote/actions'
import { getBirthdate } from 'quick-quote/insured-information/selectors'
import { getRiskEvaluated, getRPQScore } from 'quick-quote/product-investment/risk-questionnaire/selectors'
import { getAppState, isReviseQQFlow } from 'quick-quote/selectors'

/**
 * createProductCommonBenefit
 */
export function createProductCommonBenefitService() {
  /**
   * prepareBIProps - create payload for generate sale proposal
   * @param {{biType, category}} props
   * @returns {Generator<*, void, *>}
   */
  function* prepareBIProps({ biType, category }) {
    const isAuth = yield select(isLoggedIn)
    if (!isAuth) {
      return yield put(loginRequest())
    }
    return yield call(partnerApiProposal.createPayloadFromQuickQuoteState)
  }

  async function generatePDFToBase64(props, user) {
    const result = await partnerApiRequest.generateSaleProposal(user, props)
    return result.body[0].base64Content
  }

  function* generateRaForm() {
    const state = yield select(getAppState)
    const isReviseQQ = isReviseQQFlow(state)
    const user = getIdentityUser(state)

    const insured = quickQuotePolicy.insured(state)
    const rpqAnswers = quickQuotePolicy.rpqAnswers(state)
    const { funds, lstuFunds } = quickQuotePolicy.funds(state)

    const data = {
      bankBranchName: '',
      writDate: moment().format('YYYY-MM-DD'),
      policyNo: undefined,
      insureds: {
        title: insured.title,
        firstName: insured.firstName,
        lastName: insured.lastName,
        mobileNumber: undefined,
        email: undefined,
      },
      fundsInfo: funds.map((f, i) => ({
        fundCode: f.fundCode,
        fundSequence: i + 1,
      })),
      lstuFundsInfo: lstuFunds.map((f, i) => ({
        fundCode: f.fundCode,
        fundSequence: i + 1,
      })),
      rpqAnswers: rpqAnswers,
      signWithPwd: isReviseQQ ? null : generatePassword(getBirthdate(state)),
      distributionChannel: 'AZ',
    }

    const result = yield call(partnerApiRequest.generateRADocument, user, data)
    return result.base64Content
  }

  function* generateRPQForm() {
    const state = yield select(getAppState)
    const isReviseQQ = isReviseQQFlow(state)
    const user = getIdentityUser(state)

    const insured = quickQuotePolicy.insured(state)
    const rpqAnswers = quickQuotePolicy.rpqAnswers(state)
    const seller = yield call(quickQuotePolicy.seller, state)
    const riskEvaluated = getRiskEvaluated(state)
    const riskScore = getRPQScore(state)

    const data = {
      insureds: {
        title: insured.title,
        firstName: insured.firstName,
        lastName: insured.lastName,
        mobileNumber: undefined,
        email: undefined,
      },
      rpqAnswers: rpqAnswers,
      rpqFundRiskScore: riskScore,
      rpqFundRiskLevel: riskEvaluated.value === 1 ? '1' : `1-${riskEvaluated.value}`,
      bankBranchName: '',
      writDate: moment().format('YYYY-MM-DD'),
      sellerInfo: {
        brokerTitle: seller.brokerTitle,
        brokerFirstName: seller.brokerFirstName,
        brokerLastName: seller.brokerLastName,
        brokerCode: seller.brokerCode,
        brokerLicenseNo: seller.brokerLicenseNo,
        icLicense: seller.icLicense,
      },
      policyNo: undefined,
      signWithPwd: isReviseQQ ? null : generatePassword(getBirthdate(state)),
      distributionChannel: 'AZ',
    }

    const result = yield call(partnerApiRequest.generateRPQDocument, user, data)
    return result.base64Content
  }

  /**
   *
   * @param {{ pdfType: 'ra' | 'rpq' }} props
   * @returns {Generator<*, void, *>}
   */
  function* generateRaAndRpqForm({ pdfType }) {
    if (pdfType === 'rpq') {
      return yield call(generateRPQForm)
    } else if (pdfType === 'ra') {
      return yield call(generateRaForm)
    } else {
      throw Error('invalid pdfType on openDocumentPDF')
    }
  }

  return {
    prepareBIProps,
    generatePDFToBase64,
    generateRaAndRpqForm,
  }
}
